import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import AlertMessage from '../../components/AlertMessage';

// Helper to read any cookie by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  /*const FRONTEND_URL = process.env.REACT_APP_CALLBACK_URL;*/

  // OAuth Client IDs
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_OAUTH_CLIENT_ID;
  const FACEBOOK_CLIENT_ID = process.env.REACT_APP_FACEBOOK_OAUTH_CLIENT_ID;

  // Check for error parameter in URL (from OAuth callback)
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    if (error === 'auth_failed') {
      setAlert({
        type: 'error',
        message: 'Authentication failed. Please try again.',
        title: 'Login Failed',
        duration: 2500
      });
      window.history.replaceState({}, document.title, '/login');
    } else if (error === 'unknown_provider') {
      setAlert({
        type: 'error',
        message: 'Unknown authentication provider.',
        title: 'Login Failed',
        duration: 2500
      });
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location]);

  // Create axios instance with base configuration and credentials
  const api = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    }
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (alert) setAlert(null);
  };

  // Fetch user profile using the JWT access token
  const fetchUserProfile = async (accessToken) => {
    try {
      const response = await axios.get(`${API_URL}/rest-auth/user-profile/details/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      return {
        profile: {
          phone: '',
          address: '',
          company_name: '',
          contact_person: '',
          created_at: null,
          updated_at: null
        }
      };
    }
  };

  // Regular form login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    setLoading(true);
    setNeedsVerification(false);

    try {
      // Step 1: Login — read the csrftoken cookie and attach it as a header.
      // The cookie is always present (set by a prior GET through nginx/Django),
      // but Django also requires it echoed back in the X-CSRFToken header.
      const csrfToken = getCookie('csrftoken');

      const loginResponse = await api.post(
        '/rest-auth/signin/',
        {
          username: formData.username,
          password: formData.password
        },
        {
          headers: {
            // Attach the token as a header so Django's CSRF middleware can
            // compare it against the cookie value and accept the request.
            ...(csrfToken && { 'X-CSRFToken': csrfToken }),
          }
        }
      );

      // JWT response returns `access` + `refresh` (not the old `token`)
      const accessToken = loginResponse.data.access;
      const refreshToken = loginResponse.data.refresh;
      const user = loginResponse.data.user;

      // Step 2: Store token FIRST so any queries that fire during/after
      // the redirect find a valid token in localStorage immediately.
      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('authTokenStoredAt', Date.now().toString());
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userId', user.id);
      localStorage.setItem('username', user.username);
      if (user.is_superuser) {
        localStorage.setItem('is_superuser', 'true');
      }

      // Step 3: Fetch user profile data (token is now in localStorage)
      const profileData = await fetchUserProfile(accessToken);

      // Step 4: Combine user data with profile data and update localStorage
      const userWithProfile = {
        ...user,
        profile: profileData.profile || {
          phone: '',
          address: '',
          company_name: '',
          contact_person: '',
          created_at: null,
          updated_at: null
        }
      };
      localStorage.setItem('user', JSON.stringify(userWithProfile));

      setAlert({
        type: 'success',
        message: 'Login successful! Redirecting...',
        title: 'Success!',
        duration: 2500
      });

      // Step 5: Check user role and redirect accordingly
      const isSuperuser = user.is_superuser || user.is_staff;
      const redirectPath = isSuperuser ? '/dashboard' : '/client-dashboard';

      setTimeout(() => {
        navigate(redirectPath);
      }, 2500);

    } catch (err) {
      if (err.response) {
        const data = err.response.data;

        if (data.needs_verification) {
          navigate('/verify-email', {
            state: {
              email: data.email,
              fromLogin: true
            }
          });
        } else {
          setAlert({
            type: 'error',
            message: data.detail || data.non_field_errors?.[0] || 'Invalid credentials. Please try again.',
            title: 'Login Failed',
            duration: 2500
          });
        }
      } else if (err.request) {
        setAlert({
          type: 'error',
          message: 'Unable to connect to the server. Please try again later.',
          title: 'Connection Error',
          duration: 2500
        });
      } else {
        setAlert({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
          title: 'Error',
          duration: 2500
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await api.post('/rest-auth/resend-verification/', {});

      setAlert({
        type: 'success',
        message: 'Verification email sent! Please check your inbox.',
        title: 'Email Sent!',
        duration: 2500
      });

    } catch (err) {
      if (err.response) {
        const data = err.response.data;
        setAlert({
          type: 'error',
          message: data.detail || 'Failed to resend verification email.',
          title: 'Error',
          duration: 2500
        });
      } else if (err.request) {
        setAlert({
          type: 'error',
          message: 'Unable to resend verification email. Please try again.',
          title: 'Connection Error',
          duration: 2500
        });
      } else {
        setAlert({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
          title: 'Error',
          duration: 2500
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Social login handlers
  const handleGoogleLogin = () => {
    const redirectUri = `${API_URL}/`;
    const scope = 'openid profile email';
    const state = `google_${Date.now()}`;

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${GOOGLE_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scope)}` +
      `&state=${state}` +
      `&access_type=online`;

    window.location.href = authUrl;
  };

  const handleGithubLogin = () => {
    const redirectUri = `${API_URL}/`;
    const scope = 'read:user user:email';
    const state = `github_${Date.now()}`;

    const authUrl = `https://github.com/login/oauth/authorize?` +
      `client_id=${GITHUB_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&state=${state}`;

    window.location.href = authUrl;
  };

  const handleFacebookLogin = () => {
    const redirectUri = `${API_URL}/`;
    const scope = 'email,public_profile';
    const state = `facebook_${Date.now()}`;

    const authUrl = `https://www.facebook.com/v12.0/dialog/oauth?` +
      `client_id=${FACEBOOK_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&state=${state}` +
      `&response_type=code`;

    window.location.href = authUrl;
  };

  return (
    <>
      {alert && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          title={alert.title}
          duration={alert.duration}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="min-h-screen flex items-center bg-gradient-to-br from-green-50 justify-center bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="w-full max-w-md pt-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome Back
              </h2>
              <p className="text-gray-600">
                Sign in to access your account
              </p>
            </div>

            {/* Verification Notice */}
            {needsVerification && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-yellow-800">
                      Email verification required
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Please check your email and verify your account.
                    </p>
                    <button
                      onClick={handleResendVerification}
                      disabled={loading}
                      className="text-sm font-medium text-yellow-800 hover:text-yellow-900 underline mt-2"
                    >
                      Resend verification email
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Social Login Buttons */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {/* Google */}
              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span className="ml-2 text-sm font-medium text-gray-700 hidden sm:inline">Google</span>
              </button>

              {/* Facebook */}
              <button
                onClick={handleFacebookLogin}
                disabled={loading}
                className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span className="ml-2 text-sm font-medium text-gray-700 hidden sm:inline">Facebook</span>
              </button>

              {/* GitHub */}
              <button
                onClick={handleGithubLogin}
                disabled={loading}
                className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M12 0c-6.626 0-12 5.373-12 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                <span className="ml-2 text-sm font-medium text-gray-700 hidden sm:inline">GitHub</span>
              </button>
            </div>

            {/* Divider */}
            <div className="mb-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">
                    Or continue with email
                  </span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Username Input */}
              <div className="space-y-2">
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Username or Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors disabled:opacity-50"
                    placeholder="Enter your username or email"
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-sm text-green-600 hover:text-green-700 transition-colors">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors disabled:opacity-50"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center disabled:opacity-50"
                    onClick={togglePasswordVisibility}
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                        <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={loading}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded bg-gray-50 disabled:opacity-50"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Signup Link */}
            <div className="text-center pt-6 border-t border-gray-200 mt-8">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="font-medium text-green-600 hover:text-green-700 transition-colors"
                >
                  Sign up now
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;